import React, { useEffect, useState, useRef } from "react";
import CustomDataTable from "../components/custom-data-table";
import {
  dummyDataNavigationOptions,
  dummyMarketColumnNames,
  dummyTradeColumnNames,
} from "../utils/dummyData";
//import { LogoutRounded } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { FinxaiTrade } from "../models/finxaiTrade";
import DataGridWebSocketService from "../api/services/data-grid-websocket-service";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import {
  addMissingGroupValuesToMarketData,
  addOrUpdateMarketData,
  addOrUpdateStrategy,
  addOrUpdateTradeOrder,
  postLogoutDataCleanup,
  setMarketDataTableGroups,
  setTableKey,
} from "../store/slices/dataSlice";
import {
  setCurrentTableIndex,
  setDataFromWsLoading,
} from "../store/slices/uiSlice";
import {
  finxai_mkt_data,
  finxai_strategy,
  finxai_trade,
} from "../models/protobuff-objects/finxai_web_pb";
import {
  getWebSocketInstance,
  logout,
  setLoginError,
  setLoginSuccess,
} from "../store/slices/userSlice";
import { FinxaiMktData } from "../models/finxaiMktData";
import {
  addGroup,
  addNewDataId,
  clearChangedCells,
  clearNewDataIds,
  setGroups,
  setModalOpen,
} from "../store/slices/dataTableSlice";
import { clearAllCache, getCache, setCache } from "../utils/cache";
import { FinxAiUser } from "../models/finxaiUser";
import { FinxaiStrategy } from "../models/finxaiStrategy";
import { FinxaiInstrument } from "../models/finxaiInstrument";
import CustomPopUpModal from "../components/custom-popup-modal";
import GroupCreationForm from "../components/group-creation-form";
import { FinxaiDataGroup } from "../models/data-group";
import CustomSpeedDial from "../components/custom-speed-dial";
import {
  DynamicLogoWithText,
  FinxAiLogoWithText,
} from "../components/finxai-logo-with-text";
import logger from "../utils/logger";
import { ResearchView } from "./research-view";
import { FinxAiFooter } from "../components/finxai-footer";
import {
  CustomChartComponent,
  dummyChartData,
} from "../components/custom-chart";
import { convertTableGroups, extractUniqueElementsFromTableGroups } from "../utils/helpers";
import Disclaimer from "../components/disclaimer";


export const GridDataView = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { tradeOrders, marketData, tableKey, strategies, marketDataGroups } = useSelector(
    (state: RootState) => state.data
  );
  const navigate = useNavigate();
  const { isDataFromWsLoading, currentTableIndex } = useSelector(
    (state: RootState) => state.ui
  );

  const { modalOpen, groups } = useSelector(
    (state: RootState) => state.dataTable
  );
  const tableData = [
    { col: dummyTradeColumnNames, data: tradeOrders, uid: "order_id" },
    { col: dummyMarketColumnNames, data: marketData, uid: "security_id" },
  ];

  const { loggedInUser } = useSelector(
    (state: RootState) => state.user
  );

  //const loggedInUser = useSelector((state: RootState) => state.user.loggedInUser) ?? dummyAdminUser;

  const [isUserLoaded, setIsUserLoaded] = useState<boolean>(false);


  // Set initial index based on route path
  useEffect(() => {
    let initialIndex = 0;
    const path = location.pathname;

    switch (path) {
      case '/research':
        initialIndex = 2;
        break;
      case '/market':
        initialIndex = 1;
        break;
      case '/grid':
      case '/home':
      default:
        initialIndex = 0;
    }

    dispatch(setTableKey(tableKey + 1));
    dispatch(clearChangedCells());
    dispatch(setCurrentTableIndex(initialIndex));
  }, [location.pathname]);


  const handleUserCache = () => {
    const savedUser = getCache("loggedInUser");

    console.log("user role:", loggedInUser?.userRole);

    if (savedUser) {
      logger.debug("User exists in cache");
      dispatch(setLoginSuccess(savedUser as FinxAiUser));
      setIsUserLoaded(true);
    } else {
      logger.debug("User not found in cache, redirecting to login");
      navigate("/");
    }
  };

  useEffect(() => {
    handleUserCache();
  }, []);

  const ws = getWebSocketInstance();

  /// Send subscribe request to Websocket server
  useEffect(() => {
    if (isUserLoaded) {
      handleDataSubscription();
      handleTableGroups();
      getSavedGroupLayout();
      setTimeout(() => {
        dispatch(setDataFromWsLoading(false));
        logger.debug("Data subscription initialized");
        reloadStateTradeOrders();
      }, 150);
    }
  }, [isUserLoaded, loggedInUser]);

  const reloadStateTradeOrders = () => {
    logger.debug("Trade orders bid and offer price data mapped");
  };

  const handleTradeDataReceived = (order: FinxaiTrade) => {
    const isReplacement = tradeOrders.find(
      (existingOrder) => existingOrder.order_id === order.order_id
    );
    dispatch(addOrUpdateTradeOrder(order));
    /// Only add New Data ID if the incoming data is new and
    /// not replaceing existing one
    if (!isReplacement) {
      dispatch(addNewDataId(order.order_id));
    }

    /// To refresh table state whenever new data arrives

    /// To clear the new data animation after 3 seconds
    setTimeout(() => {
      dispatch(clearNewDataIds());
    }, 3000);
    logger.debug("Handled trade order");
  };

  const handleMarketDataReceived = (data: FinxaiMktData) => {
    dispatch(addOrUpdateMarketData(data));
  };

  const handleStrategiesDataRecevied = (data: FinxaiStrategy) => {
    dispatch(addOrUpdateStrategy(data));
  };

  function handleDataSubscription() {
    if (loggedInUser && isUserLoaded) {
      const subReq = ws.buildSubscriptionRequest(loggedInUser);
      /// Send subscription request
      DataGridWebSocketService.sendMessage(subReq.serializeBinary());
      logger.debug("SUB REQUEST SENT SUCCESSFULLY!");
    }
  };

  useEffect(() => {
    DataGridWebSocketService.sendOnMessage(async (message) => {
      const payload = await ws.handleBinaryMessage(message.data);
      if (payload instanceof finxai_trade) {
        logger.error("Received Trade data from WebSocket");
        const order = ws.buildObject(
          payload,
          marketData,
          strategies
        ) as FinxaiTrade;
        logger.debug("Trade order update:", order);
        handleTradeDataReceived(order);
      }

      if (payload instanceof finxai_mkt_data) {
        // logger.debug("Received MARKET data from WebSocket");
        const mktData = ws.buildObject(
          payload,
          marketData,
          strategies
        ) as FinxaiMktData;
        
        if (Math.random() < 0.01) { // Execute for approximately every 100th time
          logger.debug("Market data update:", mktData);
          handleMarketDataReceived(mktData);
        }
      }

      if (payload instanceof finxai_strategy) {
        logger.debug("RECEIVED FINXAISTATEGY Payload");
        const strategy = ws.buildObject(
          payload,
          marketData,
          strategies
        ) as FinxaiStrategy;
        logger.debug("Strategy update:", strategy);
        handleStrategiesDataRecevied(strategy);
      }
    })
  });

  //endpoint
  const endpoint = process.env.REACT_APP_WEB_SOCKET_ENDPOINT as string;

  //logout
  const handleLogout = () => {
    const logoutPayload = ws.buildLogoutRequest(loggedInUser!);
    /// Send logout request to ws server first
    DataGridWebSocketService.sendMessage(logoutPayload.serializeBinary());
    /// Clear the locally in-memory cached user
    dispatch(logout());
    /// Clear in memory data
    dispatch(postLogoutDataCleanup());
    /// Clear all user related browser cache
    clearAllCache();
    navigate("/");
    //clear login error when logged out
    dispatch(setLoginError(""));
  };

  function saveCurrentGroupLayout() {
    if (loggedInUser != null && loggedInUser.username != null) {
      setCache(`${loggedInUser.username}grouplayout`, groups);
      logger.debug("Saved layout!");
    }
  }

  function getSavedGroupLayout() {
    if (loggedInUser != null && loggedInUser.username != null) {
      const cached = getCache(`${loggedInUser.username}grouplayout`);
      if (cached) {
        logger.debug("Cached data exists:", cached);
        dispatch(setGroups(cached));
      } else {
        logger.debug("No data present");
      }
    }
  }

  function handleGroupCreation(group: FinxaiDataGroup) {
    dispatch(addGroup(group));
    dispatch(setModalOpen(false));
    saveCurrentGroupLayout();
  }

  const handleClose = () => dispatch(setModalOpen(false));
  const handleOpen = () => dispatch(setModalOpen(true));

  let localTableKey = 0;
  function handleTableGroups() {
    const tableGroups = convertTableGroups(loggedInUser?.userPreferences ?? "", "market_data", "Instrument");
    dispatch(setMarketDataTableGroups(tableGroups));
    const uniqueElements = extractUniqueElementsFromTableGroups(tableGroups);
    dispatch(addMissingGroupValuesToMarketData({ groupNames: uniqueElements }));
    localTableKey += 1;

  }

  /////user dropdown menu
  const [isOpen, setIsOpen] = useState(false);
  const dropdowRef = useRef<HTMLDivElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);


  //toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  //upload file as binary and save it to websocket

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {

    if (!selectedFile) {
      console.warn("No file selected");
      return
    }
    try {
      const binaryData = await ws.sendFile(selectedFile);
      alert("Files is uploaded!")
      console.log("Binary data ready:", binaryData)

    } catch (error) {
      console.error("error reading file:", error)
    }

  };

  return (
    <div className="h-screen fledx flex-col">
      {/* Header section with username, logout button, and logo */}
      <div className="flex justify-between items-center w-full">
        {/* Left-aligned username and logout button */}
        <div className="flex items-center ml-auto">
          <div className="bg-tableHeaderBg w-min rounded-bl-xl p-3 items-end justify-end flex text-white">
            <button
              onClick={toggleDropdown}
              className="mr-2">
              {loggedInUser?.username}
            </button>

            {/*Dropdown*/}
            {isOpen && (
              <div
                ref={dropdowRef}
                className="absolute right-10 top-16 w-40 bg-tableHeaderBg shadow-lg rounded p-4">
                {/*logout button*/}
                <button onClick={() => handleLogout()} className="text-white-500 underline">
                  Log out
                </button>

                {/*upload file for admin only*/}

                <div>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="text-white-500 underline"
                  />
                  <button
                    onClick={handleUpload}
                    className="text-white-500 underline"
                  >
                    Upload
                  </button>
                </div>




              </div>
            )}
          </div>

        </div>

        {/* Centered logo */}
        <div className="absolute left-1/2 transform -translate-x-1/2 pt-2">
          <DynamicLogoWithText />
        </div>
      </div>

      <CustomPopUpModal isOpen={modalOpen} onClose={handleClose}>
        <GroupCreationForm
          onSubmit={(group) => {
            handleGroupCreation(group);
            logger.debug(
              `Group name : ${group.groupName}, Selected Ticker: ${group.tickers}`
            );
          }}
          optionNames={Array.from(
            new Set(
              tradeOrders
                .filter((e) => e.ticker_symbol !== "N.A")
                .map((e) => e.order_id)
            )
          )}
        />
      </CustomPopUpModal>

      <div>
        <button onClick={() => navigate('/terminal')}>Terminal</button>
      </div>

      <div className="justify-center items-center flex p-5 bg-primarybg flex-col">
        <div className="p-2 rounded-lg flex-row flex">
          {dummyDataNavigationOptions.map((option, index) => (
            <button
              key={index}
              onClick={() => {
                dispatch(setTableKey(tableKey + 1));
                dispatch(clearChangedCells());
                return dispatch(setCurrentTableIndex(index));
              }}
              style={{
                transition: "color 0.5s ease-in-out, border 0.5s ease-in-out",
              }}
              className={`${currentTableIndex === index
                ? "text-primary"
                : "text-cellTextFontColor"
                } m-2 rounded-2xl ${index === currentTableIndex
                  ? "border-solid border-2 border-primary"
                  : "border-2 border-transparent"
                }`}
            >
              <nav className="m-2 font-bold">{option}</nav>
            </button>
          ))}
        </div>
        <div>
          <Disclaimer />
        </div>
        {isDataFromWsLoading ? (
          <div className="text-white text-2xl">Loading...</div>
        ) : currentTableIndex === 2 ? (
          <div style={{ height: "100vh", width: "100vw" }}>
            <ResearchView url={`${endpoint}/research/index.html`} />

          </div>
        ) : (
          <div className="pb-24">
            <CustomDataTable
              key={localTableKey}
              username={loggedInUser?.username}
              enableGrouping={currentTableIndex === 1}
              enableTableGrouping={currentTableIndex === 1}
              tableGroups={marketDataGroups}
              allTableGroupsPreExpanded={true}
              columns={tableData[currentTableIndex].col}
              data={tableData[currentTableIndex].data}
              uid={tableData[currentTableIndex].uid}
              preExpandedGroups={["Ungrouped"]}
              excludeData={{
                Instrument: ["N.A", "RestrictedInst"],
              }}
              onRowTap={(e) => {
              }}
            />
          </div>
        )}
      </div>

      {/* {currentTableIndex === 0 && (
        <CustomSpeedDial onOpenClick={handleOpen} groups={groups} />
      )} */}

      <FinxAiFooter></FinxAiFooter>
    </div>
  );
};

export default GridDataView;
